/**
 * Admin-console roles, carried in the Firebase `role` claim (set by staffSignIn after Google sign-in)
 * and enforced again by every subgraph (@Roles).
 */
export type StaffRole = 'admin' | 'manager' | 'viewer';

export const STAFF_ROLES: StaffRole[] = ['admin', 'manager', 'viewer'];

export const ROLE_LABEL: Record<StaffRole, string> = {
  admin: 'Admin',
  manager: 'Manager',
  viewer: 'Viewer',
};

export const ROLE_DESCRIPTION: Record<StaffRole, string> = {
  admin: 'Everything, including console access, points rules, push messages and settings',
  manager: 'Everything except console access, points rules, push messages and settings',
  viewer: 'Insight dashboards only',
};

export type Permission =
  | 'stats:view'
  | 'moderation:manage'
  | 'users:view'
  | 'users:block'
  | 'users:delete'
  | 'access:manage'
  | 'catalog:manage'
  | 'content:manage'
  | 'points:manage'
  | 'push:manage'
  | 'settings:manage';

/** Mirrors the backend @Roles(...) decorators; the UI only hides what the server would reject anyway. */
export const PERMISSIONS: Record<Permission, StaffRole[]> = {
  'stats:view': ['admin', 'manager', 'viewer'],
  'moderation:manage': ['admin', 'manager'],
  'users:view': ['admin', 'manager'],
  'users:block': ['admin', 'manager'],
  'users:delete': ['admin'],
  'access:manage': ['admin'],
  'catalog:manage': ['admin', 'manager'],
  'content:manage': ['admin', 'manager'],
  'points:manage': ['admin'],
  'push:manage': ['admin'],
  'settings:manage': ['admin'],
};

export const can = (role: StaffRole | undefined, permission: Permission): boolean => !!role && PERMISSIONS[permission].includes(role);

export const isStaffRole = (r: unknown): r is StaffRole => typeof r === 'string' && (STAFF_ROLES as string[]).includes(r);
